import {useState} from "react";
import {Box, Collapse, Typography, useTheme} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";

export const claimKey = (c) => `${c.claimType}::${c.claimValue}`;

// Read-only preview of every permission granted by the selected groups, grouped by claim category
// (ordered by display order) into collapsed-by-default accordions. Each permission carries one
// color-coded dot per granting group so overlaps are visible. Shared by Step 3 and Step 5.
//
// Props:
//   selectedGroups: [{id, name, color, claims: [{claimType, claimValue, displayName, categoryId}]}]
//   categories:     [{id, displayName, displayOrder, claimCount}]
const PermissionsPreview = ({selectedGroups = [], categories = []}) => {
    const theme = useTheme();
    const [openCats, setOpenCats] = useState({});

    // Aggregate granted permissions: claimKey -> {claim, groups: [{id, name, color}]}.
    const byClaim = new Map();
    selectedGroups.forEach((g) => {
        (g.claims ?? []).forEach((c) => {
            const k = claimKey(c);
            if (!byClaim.has(k)) byClaim.set(k, {claim: c, groups: []});
            const entry = byClaim.get(k);
            if (!entry.groups.some((x) => x.id === g.id)) {
                entry.groups.push({id: g.id, name: g.name, color: g.color});
            }
        });
    });

    // Bucket by category, keeping only categories that have at least one granted permission.
    const claimsByCat = new Map();
    byClaim.forEach(({claim, groups}) => {
        if (!claimsByCat.has(claim.categoryId)) claimsByCat.set(claim.categoryId, []);
        claimsByCat.get(claim.categoryId).push({claim, groups});
    });
    const catsToShow = [...categories]
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .filter((cat) => (claimsByCat.get(cat.id) ?? []).length > 0);

    if (selectedGroups.length === 0 || byClaim.size === 0) {
        return (
            <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>
                No permissions to preview.
            </Typography>
        );
    }

    const toggle = (id) => setOpenCats((s) => ({...s, [id]: !s[id]}));

    return (
        <Box>
            {/* Legend: which color maps to which group */}
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5}}>
                {selectedGroups.map((g) => (
                    <Box key={g.id} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: g.color}} />
                        <Typography sx={{fontSize: "0.78rem"}}>{g.name}</Typography>
                    </Box>
                ))}
            </Box>

            {catsToShow.map((cat) => {
                const rows = [...(claimsByCat.get(cat.id) ?? [])].sort((a, b) =>
                    (a.claim.displayName || a.claim.claimValue).localeCompare(b.claim.displayName || b.claim.claimValue)
                );
                const open = !!openCats[cat.id];
                const total = cat.claimCount ?? rows.length;
                return (
                    <Box key={cat.id} sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "6px", mb: 1, overflow: "hidden"}}>
                        <Box onClick={() => toggle(cat.id)}
                             sx={{display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, cursor: "pointer", backgroundColor: theme.palette.background.secondary}}>
                            {open ? <KeyboardArrowDownOutlinedIcon fontSize="small" /> : <KeyboardArrowRightOutlinedIcon fontSize="small" />}
                            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em"}}>
                                {cat.displayName}
                            </Typography>
                            <Typography sx={{ml: "auto", fontSize: "0.78rem", color: "text.secondary", fontWeight: 700}}>
                                {rows.length}/{total}
                            </Typography>
                        </Box>
                        <Collapse in={open}>
                            <Box sx={{p: 1.5, display: "flex", flexDirection: "column", gap: 0.75}}>
                                {rows.map(({claim, groups}) => (
                                    <Box key={claimKey(claim)} sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <Box sx={{display: "flex", gap: 0.4}}>
                                            {groups.map((g) => (
                                                <Box key={g.id} title={g.name}
                                                     sx={{width: 9, height: 9, borderRadius: "50%", backgroundColor: g.color, flex: "0 0 9px"}} />
                                            ))}
                                        </Box>
                                        <Typography sx={{fontSize: "0.85rem"}}>{claim.displayName || claim.claimValue}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
};

export default PermissionsPreview;
